/**
 * offlineStore — tiny IndexedDB-backed cache for media files so users
 * can download audiobooks / cinematic episodes and play them without
 * network access.
 *
 * Why IndexedDB and not Cache Storage?
 *  - Cache Storage keys by Request (URL) which is fine, but its storage
 *    quota on iOS Safari is more aggressive than IndexedDB's and blobs
 *    can be evicted unpredictably.
 *  - We also need to store lightweight metadata (title, cover, duration,
 *    saved_at) alongside each blob for the "Downloaded" tab — IndexedDB
 *    lets us query that cleanly.
 *
 * Layout:
 *  - Database: `smarter_offline_v1`
 *  - Store: `media` (keyPath: id)
 *    Each row: { id, url, blob, meta, saved_at, mime }
 *
 * All methods are promise-based, silently no-op on missing IndexedDB.
 */

const DB_NAME = 'smarter_offline_v1';
const STORE = 'media';
const DB_VERSION = 1;

let dbPromise = null;
const openDb = () => {
  if (!('indexedDB' in window)) return Promise.resolve(null);
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
};

const withStore = async (mode, fn) => {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const store = tx.objectStore(STORE);
    const result = fn(store);
    tx.oncomplete = () => resolve(result);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
};

export const hasOffline = async (id) => {
  if (!id) return false;
  try {
    const row = await withStore('readonly', (s) => {
      return new Promise((res) => {
        const req = s.get(id);
        req.onsuccess = () => res(req.result);
        req.onerror = () => res(null);
      });
    });
    return !!(row && row.blob);
  } catch {
    return false;
  }
};

/**
 * Stream-download the given URL into IndexedDB, reporting progress.
 * Returns the stored blob URL (object URL) the caller can use immediately.
 */
export const saveOffline = async (id, url, meta = {}, onProgress) => {
  if (!id || !url) throw new Error('id + url required');
  const db = await openDb();
  if (!db) throw new Error('IndexedDB not available');

  // Resolve relative URLs against the backend origin.
  const base = process.env.REACT_APP_BACKEND_URL || '';
  let absolute = url;
  if (absolute.startsWith('/uploads/')) absolute = `${base}/api${absolute}`;
  else if (absolute.startsWith('/api/')) absolute = `${base}${absolute}`;

  const resp = await fetch(absolute, { credentials: 'include' });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  const total = Number(resp.headers.get('content-length')) || 0;
  const mime = resp.headers.get('content-type') || 'application/octet-stream';
  const reader = resp.body?.getReader();
  if (!reader) {
    // Older browsers without streams — fall back to full buffer.
    const blob = await resp.blob();
    await putRow(id, { id, url, blob, mime, meta, saved_at: Date.now() });
    onProgress?.({ loaded: blob.size, total: blob.size, pct: 100 });
    return URL.createObjectURL(blob);
  }
  const chunks = [];
  let loaded = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    // eslint-disable-next-line no-await-in-loop
    const { value, done } = await reader.read();
    if (done) break;
    chunks.push(value);
    loaded += value.length;
    onProgress?.({
      loaded,
      total,
      pct: total ? Math.round((loaded / total) * 100) : null,
    });
  }
  const blob = new Blob(chunks, { type: mime });
  await putRow(id, { id, url, blob, mime, meta, saved_at: Date.now() });
  return URL.createObjectURL(blob);
};

const putRow = (id, row) =>
  withStore('readwrite', (s) => new Promise((res, rej) => {
    const req = s.put(row);
    req.onsuccess = () => res(true);
    req.onerror = () => rej(req.error);
  }));

export const getOfflineUrl = async (id) => {
  try {
    const row = await withStore('readonly', (s) => new Promise((res) => {
      const req = s.get(id);
      req.onsuccess = () => res(req.result);
      req.onerror = () => res(null);
    }));
    if (!row || !row.blob) return null;
    return URL.createObjectURL(row.blob);
  } catch {
    return null;
  }
};

export const removeOffline = async (id) => {
  if (!id) return false;
  try {
    await withStore('readwrite', (s) => {
      s.delete(id);
    });
    return true;
  } catch {
    return false;
  }
};

/** List every downloaded item (metadata only — blobs excluded for speed). */
export const listOffline = async () => {
  try {
    const rows = await withStore('readonly', (s) => new Promise((res) => {
      const out = [];
      const req = s.openCursor();
      req.onsuccess = () => {
        const cur = req.result;
        if (cur) {
          const v = cur.value;
          out.push({
            id: v.id,
            url: v.url,
            meta: v.meta,
            saved_at: v.saved_at,
            size: v.blob?.size || 0,
            mime: v.mime,
          });
          cur.continue();
        } else {
          res(out);
        }
      };
      req.onerror = () => res([]);
    }));
    return rows || [];
  } catch {
    return [];
  }
};

/** Total bytes currently cached on this device (used in settings). */
export const getOfflineSize = async () => {
  const items = await listOffline();
  return items.reduce((sum, it) => sum + (it.size || 0), 0);
};
